import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Auth0Provider } from "@auth0/auth0-react";
import "./App.css";

import { AuthProvider } from "./context/AuthContext";
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import { PublicAuthProvider, usePublicAuth } from "./context/PublicAuthContext";
import { TopHypeProvider } from "./context/TopHypeContext";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import MaintenanceGate from "./components/MaintenanceGate";
import OnboardingModal from "./components/OnboardingModal";

import HomePage from "./pages/HomePage";
import SearchPage from "./pages/SearchPage";
import ReleasePage from "./pages/ReleasePage";
import ArtistPage from "./pages/ArtistPage";
import PublicArtistsPage from "./pages/ArtistsPage";
import CustomPage from "./pages/CustomPage";
import ShortLinkRedirect from "./pages/ShortLinkRedirect";
import PublicLoginPage from "./pages/PublicLoginPage";
import SupporterPage from "./pages/SupporterPage";
import SupporterSuccessPage from "./pages/SupporterSuccessPage";
import UserProfilePage from "./pages/UserProfilePage";
import ArtistStudioPage from "./pages/ArtistStudioPage";
import AdminBioApprovalsPage from "./pages/AdminBioApprovalsPage";
import ReleaseProposalsPage from "./pages/admin/ReleaseProposalsPage";
import ImportExportPage from "./pages/admin/ImportExportPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import UserPublicProfilePage from "./pages/UserPublicProfilePage";
import UserFavoritesPage from "./pages/UserFavoritesPage";
import UserFollowsPage from "./pages/UserFollowsPage";
import CommunityPage from "./pages/CommunityPage";
import ChartsPage from "./pages/ChartsPage";
import AdminArtistApplicationsPage from "./pages/AdminArtistApplicationsPage";
import NotFoundPage from "./pages/NotFoundPage";
import LabelsPage from "./pages/LabelsPage";
import LabelPage from "./pages/LabelPage";
import LabelStudioPage from "./pages/LabelStudioPage";
import FuerLabelsPage from "./pages/FuerLabelsPage";

import AdminLoginPage from "./pages/admin/LoginPage";
import Dashboard from "./pages/admin/Dashboard";
import ReleasesPage from "./pages/admin/ReleasesPage";
import ReleaseFormPage from "./pages/admin/ReleaseFormPage";
import ArtistsPage from "./pages/admin/ArtistsPage";
import ArtistFormPage from "./pages/admin/ArtistFormPage";
import PagesAdminPage from "./pages/admin/PagesAdminPage";
import ProfilePage from "./pages/admin/ProfilePage";
import AffiliateAdminPage from "./pages/admin/AffiliateAdminPage";
import StripeSyncPage from "./pages/admin/StripeSyncPage";
import UsersAdminPage from "./pages/admin/UsersAdminPage";
import SiteSettingsPage from "./pages/admin/SiteSettingsPage";
import MediaPage from "./pages/admin/MediaPage";
import PartnerPage from "./pages/admin/PartnerPage";
import BackupAdminPage from "./pages/admin/BackupAdminPage";
import SpotlightAdminPage from "./pages/admin/SpotlightAdminPage";
import SpotifyPlaylistPage from "./pages/admin/SpotifyPlaylistPage";
import SocialMediaPage from "./pages/admin/SocialMediaPage";
import LabelsAdminPage from "./pages/admin/LabelsAdminPage";
import LabelApplicationsAdminPage from "./pages/admin/LabelApplicationsAdminPage";
import CollabFinderPage from "./pages/CollabFinderPage";
import CollabAdminPage from "./pages/admin/CollabAdminPage";
import NotificationsPage from "./pages/NotificationsPage";
import MessagesPage from "./pages/MessagesPage";

// Auth0Provider needs useNavigate, so it must be inside BrowserRouter
function Auth0ProviderWithNavigate({ children }) {
  const navigate = useNavigate();
  // redirect_uri muss EXAKT mit Allowed Callback URLs in Auth0 übereinstimmen
  // Auth0 ist konfiguriert ohne trailing slash
  const redirectUri = window.location.origin;
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: "openid profile email",
      }}
      onRedirectCallback={(appState) => navigate(appState?.returnTo || "/")}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
}

// Onboarding-Modal als Teil des Baums (braucht PublicAuthContext)
function OnboardingGate({ children }) {
  const { showOnboarding, setShowOnboarding, token, setPublicUser, publicUser } = usePublicAuth();
  if (!showOnboarding) return children;
  return (
    <>
      {children}
      <OnboardingModal
        token={token}
        publicUser={publicUser}
        onComplete={(updatedUser) => {
          if (updatedUser) setPublicUser(prev => ({ ...prev, ...updatedUser, needs_onboarding: false }));
          setShowOnboarding(false);
        }}
      />
    </>
  );
}

function TrackingScripts() {
  const { settings } = useSettings();

  // Google Analytics injection
  useEffect(() => {
    const gaId = settings?.google_analytics_id?.trim();
    const existingGA = document.getElementById("ga-script");
    const existingGAInline = document.getElementById("ga-inline");

    // Remove old scripts if GA ID changed or was cleared
    if (existingGA) existingGA.remove();
    if (existingGAInline) existingGAInline.remove();

    if (!gaId) return;

    const scriptSrc = document.createElement("script");
    scriptSrc.id = "ga-script";
    scriptSrc.async = true;
    scriptSrc.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(scriptSrc);

    const scriptInline = document.createElement("script");
    scriptInline.id = "ga-inline";
    scriptInline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
    document.head.appendChild(scriptInline);
  }, [settings?.google_analytics_id]);

  // Cookie Banner injection
  useEffect(() => {
    const code = settings?.cookie_banner_code?.trim();
    const existing = document.getElementById("cookie-banner-injected");
    if (existing) existing.remove();
    if (!code) return;

    const container = document.createElement("div");
    container.id = "cookie-banner-injected";
    container.style.display = "none";
    container.innerHTML = code;

    // Move actual scripts to head so they execute
    container.querySelectorAll("script").forEach(oldScript => {
      const newScript = document.createElement("script");
      [...oldScript.attributes].forEach(attr => newScript.setAttribute(attr.name, attr.value));
      if (oldScript.textContent) newScript.textContent = oldScript.textContent;
      document.head.appendChild(newScript);
      oldScript.remove();
    });

    document.head.appendChild(container);
  }, [settings?.cookie_banner_code]);

  // Favicon
  useEffect(() => {
    const faviconUrl = settings?.favicon_url?.trim();
    if (!faviconUrl) return;
    let link = document.getElementById("dynamic-favicon") || document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      link.id = "dynamic-favicon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
    link.type = faviconUrl.endsWith(".svg") ? "image/svg+xml" : "image/png";
  }, [settings?.favicon_url]);

  return null;
}

function App() {
  // Remove platform badge aggressively
  useEffect(() => {
    const remove = () => {
      // Target by selectors
      document.querySelectorAll('a[href*="emergent"], [class*="emergent"], [id*="emergent"]')
        .forEach(el => { el.style.display = 'none'; el.style.visibility = 'hidden'; });
      // Target by text content (find fixed-position parents)
      document.querySelectorAll('*').forEach(el => {
        if (el.children.length === 0 && el.textContent?.trim() === 'Made with Emergent') {
          let node = el;
          for (let i = 0; i < 5; i++) {
            if (!node.parentElement || node.parentElement === document.body) break;
            node = node.parentElement;
            const style = window.getComputedStyle(node);
            if (style.position === 'fixed' || style.position === 'absolute') {
              node.style.display = 'none';
              break;
            }
          }
        }
      });
    };
    remove();
    const observer = new MutationObserver(remove);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return (
    <HelmetProvider>
      <SettingsProvider>
        <BrowserRouter>
          <Auth0ProviderWithNavigate>
            {/* AuthProvider muss INNERHALB Auth0Provider liegen (nutzt useAuth0) */}
            <AuthProvider>
              <PublicAuthProvider>
                <TopHypeProvider>
                <OnboardingGate>
                <TrackingScripts />
                <ScrollToTop />
                <ScrollToTopButton />
                <Routes>
                  {/* Public */}
                  <Route path="/" element={<MaintenanceGate><HomePage /></MaintenanceGate>} />
                  <Route path="/search" element={<MaintenanceGate><SearchPage /></MaintenanceGate>} />
                  <Route path="/release/:slug" element={<MaintenanceGate><ReleasePage /></MaintenanceGate>} />
                  <Route path="/artist/:slug" element={<MaintenanceGate><ArtistPage /></MaintenanceGate>} />
                  <Route path="/artists" element={<MaintenanceGate><PublicArtistsPage /></MaintenanceGate>} />
                  <Route path="/user/:username" element={<MaintenanceGate><UserPublicProfilePage /></MaintenanceGate>} />
                  <Route path="/page/:slug" element={<MaintenanceGate><CustomPage /></MaintenanceGate>} />
                  <Route path="/r/:slug" element={<ShortLinkRedirect />} />
                  {/* Public User Auth */}
                  <Route path="/login" element={<PublicLoginPage />} />
                  <Route path="/supporter" element={<MaintenanceGate><SupporterPage /></MaintenanceGate>} />
                  <Route path="/supporter/success" element={<MaintenanceGate><SupporterSuccessPage /></MaintenanceGate>} />
                  <Route path="/profil" element={<MaintenanceGate><UserProfilePage /></MaintenanceGate>} />
                  <Route path="/profil/favoriten" element={<MaintenanceGate><UserFavoritesPage /></MaintenanceGate>} />
                  <Route path="/profil/follows" element={<MaintenanceGate><UserFollowsPage /></MaintenanceGate>} />
                  <Route path="/artist-studio" element={<MaintenanceGate><ArtistStudioPage /></MaintenanceGate>} />
                  <Route path="/studio/collabs" element={<MaintenanceGate><CollabFinderPage /></MaintenanceGate>} />
                  <Route path="/benachrichtigungen" element={<MaintenanceGate><NotificationsPage /></MaintenanceGate>} />
                  <Route path="/nachrichten" element={<MaintenanceGate><MessagesPage /></MaintenanceGate>} />
                  <Route path="/label-studio" element={<MaintenanceGate><LabelStudioPage /></MaintenanceGate>} />
                  <Route path="/labels" element={<MaintenanceGate><LabelsPage /></MaintenanceGate>} />
                  <Route path="/label/:slug" element={<MaintenanceGate><LabelPage /></MaintenanceGate>} />
                  <Route path="/fuer-labels" element={<MaintenanceGate><FuerLabelsPage /></MaintenanceGate>} />
                  <Route path="/label/:slug" element={<MaintenanceGate><LabelPage /></MaintenanceGate>} />
                  <Route path="/community" element={<MaintenanceGate><CommunityPage /></MaintenanceGate>} />
                  <Route path="/charts" element={<MaintenanceGate><ChartsPage /></MaintenanceGate>} />

                  {/* Admin */}
                  <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/admin/releases" element={<ProtectedRoute><ReleasesPage /></ProtectedRoute>} />
                  <Route path="/admin/releases/new" element={<ProtectedRoute><ReleaseFormPage /></ProtectedRoute>} />
                  <Route path="/admin/releases/:id/edit" element={<ProtectedRoute><ReleaseFormPage /></ProtectedRoute>} />
                  <Route path="/admin/artists" element={<ProtectedRoute><ArtistsPage /></ProtectedRoute>} />
                  <Route path="/admin/artists/new" element={<ProtectedRoute><ArtistFormPage /></ProtectedRoute>} />
                  <Route path="/admin/artists/:id/edit" element={<ProtectedRoute><ArtistFormPage /></ProtectedRoute>} />
                  <Route path="/admin/pages" element={<ProtectedRoute><PagesAdminPage /></ProtectedRoute>} />
                  <Route path="/admin/import-export" element={<ProtectedRoute><ImportExportPage /></ProtectedRoute>} />
                  <Route path="/admin/media" element={<ProtectedRoute requireDeveloper><MediaPage /></ProtectedRoute>} />
                  <Route path="/admin/partner" element={<ProtectedRoute requireDeveloper><PartnerPage /></ProtectedRoute>} />
                  <Route path="/admin/settings" element={<ProtectedRoute><SiteSettingsPage /></ProtectedRoute>} />
                  <Route path="/admin/affiliate" element={<ProtectedRoute requireDeveloper><AffiliateAdminPage /></ProtectedRoute>} />
                  <Route path="/admin/stripe-sync" element={<ProtectedRoute requireDeveloper><StripeSyncPage /></ProtectedRoute>} />
                  <Route path="/admin/users" element={<ProtectedRoute requireDeveloper><AdminUsersPage /></ProtectedRoute>} />
                  <Route path="/admin/artist-applications" element={<ProtectedRoute><AdminArtistApplicationsPage /></ProtectedRoute>} />
                  <Route path="/admin/bio-approvals" element={<ProtectedRoute><AdminBioApprovalsPage /></ProtectedRoute>} />
                  <Route path="/admin/release-proposals" element={<ProtectedRoute><ReleaseProposalsPage /></ProtectedRoute>} />
                  <Route path="/admin/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                  <Route path="/admin/backup" element={<ProtectedRoute requireDeveloper><BackupAdminPage /></ProtectedRoute>} />
                  <Route path="/admin/collabs" element={<ProtectedRoute requireDeveloper><CollabAdminPage /></ProtectedRoute>} />
                  <Route path="/admin/spotlight" element={<ProtectedRoute requireDeveloper><SpotlightAdminPage /></ProtectedRoute>} />
                  <Route path="/admin/spotify-playlist" element={<ProtectedRoute requireDeveloper><SpotifyPlaylistPage /></ProtectedRoute>} />
                  <Route path="/admin/social-media" element={<ProtectedRoute requireDeveloper><SocialMediaPage /></ProtectedRoute>} />
                  <Route path="/admin/labels" element={<ProtectedRoute><LabelsAdminPage /></ProtectedRoute>} />
                  <Route path="/admin/label-applications" element={<ProtectedRoute><LabelApplicationsAdminPage /></ProtectedRoute>} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </OnboardingGate>
                </TopHypeProvider>
              </PublicAuthProvider>
            </AuthProvider>
          </Auth0ProviderWithNavigate>
        </BrowserRouter>
      </SettingsProvider>
    </HelmetProvider>
  );
}

export default App;
